if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.addEventListener('click', this.handleMagnumDisabledOptionClick.bind(this));
        this.addEventListener('submit', this.handleMagnumSubmit.bind(this), true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.classList.remove('quick-add-modal--magnum-pdp');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        this.classList.toggle('quick-add-modal--magnum-pdp', productElement.classList.contains('magnum-pdp'));
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      handleMagnumDisabledOptionClick(event) {
        if (!this.classList.contains('quick-add-modal--magnum-pdp')) return;

        const label = event.target.closest('label');
        const input = event.target.matches('.magnum-pdp__variants input') ? event.target : label?.control;
        if (!input?.matches('.magnum-pdp__variants input')) return;
        if (!input.classList.contains('disabled')) {
          window.setTimeout(() => {
            if (input.checked) {
              input.dispatchEvent(new Event('change', { bubbles: true }));
              this.updateMagnumSelectedVariantState();
            }
          });
          return;
        }

        event.preventDefault();
        this.setMagnumUnavailableOption(input);
        window.setTimeout(() => this.setMagnumUnavailableOption(input));
      }

      setMagnumUnavailableOption(input) {
        input.checked = true;
        const selectedValue = input.closest('.product-form__input')?.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.innerHTML = input.value;

        const variantIdInput = this.querySelector('input[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = '';
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.querySelector('product-form')?.toggleSubmitButton(true, window.variantStrings.unavailable);
      }

      handleMagnumSubmit(event) {
        if (!this.classList.contains('quick-add-modal--magnum-pdp')) return;

        const disabledSelection = this.querySelector('.magnum-pdp__variants input.disabled:checked');
        if (!disabledSelection) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.setMagnumUnavailableOption(disabledSelection);
      }

      fetchMagnumProductJson(productInfo) {
        const productUrl = productInfo?.dataset.url;
        if (!productUrl) return Promise.resolve(null);

        if (this.magnumProductJsonUrl !== productUrl) {
          this.magnumProductJsonUrl = productUrl;
          this.magnumProductJsonPromise = fetch(`${productUrl}.js`).then((response) => response.json());
        }

        return this.magnumProductJsonPromise;
      }

      updateMagnumSelectedVariantState() {
        const productInfo = this.querySelector('product-info');
        const selectedOptions = Array.from(
          this.querySelectorAll('.magnum-pdp__variants .product-form__input')
        )
          .map((group) => group.querySelector('input:checked')?.value)
          .filter(Boolean);

        this.fetchMagnumProductJson(productInfo).then((product) => {
          if (!product) return;

          const variant = product.variants?.find((candidate) => {
            return candidate.options?.every((option, index) => option === selectedOptions[index]);
          });

          const variantIdInput = this.querySelector('input[name="id"]');
          if (variantIdInput) {
            variantIdInput.value = variant?.available ? variant.id : '';
            variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.querySelector('product-form')?.toggleSubmitButton(
            !variant?.available,
            !variant?.available ? window.variantStrings.unavailable : undefined
          );
        });
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
